/**
 * API Configuration Utility
 * Handles API URL resolution for both development and production environments
 * 
 * DEPLOYMENT MODES:
 * - localhost/127.0.0.1: Uses REACT_APP_BACKEND_URL from .env
 * - All other domains: Uses relative '/api' path (works with ingress routing)
 */

// Get the appropriate API URL based on the current domain
export const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // In development (localhost), use the env variable
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      const url = `${process.env.REACT_APP_BACKEND_URL}`;
      return url;
    }
    
    // For ALL deployed environments (preview, staging, production custom domains)
    // Return empty string - call sites already include /api prefix
    // The ingress/proxy routes /api/* to the backend service
    return '';
  }
  // Fallback for SSR or non-browser environments
  return `${process.env.REACT_APP_BACKEND_URL}`;
};

// Get the base URL for assets (images, etc.)
export const getBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // In development, use the env variable
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return process.env.REACT_APP_BACKEND_URL || '';
    }
    // For all deployed environments, use relative path (empty = same origin)
    return '';
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

export const API = getApiUrl();
export const BASE_URL = getBaseUrl();

/**
 * Format currency in Fiji Dollars (FJD)
 * @param {number} amount - The amount to format
 * @param {boolean} showCurrency - Whether to show the FJD suffix (default: true)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, showCurrency = true) => {
  const formatted = new Intl.NumberFormat('en-FJ', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
  return showCurrency ? `${formatted} FJD` : formatted;
};

/**
 * Format price with +/- prefix for modifiers
 * @param {number} price - The price to format
 * @returns {string} Formatted price string with prefix
 */
export const formatPriceModifier = (price) => {
  if (price === 0) return 'Included';
  const formatted = Math.abs(price).toFixed(2);
  if (price > 0) return `+${formatted} FJD`;
  return `-${formatted} FJD`;
};

// Get auth headers for admin API calls
export const getAuthHeaders = () => {
  const adminData = localStorage.getItem('bulaccino-admin');
  let token = '';
  if (adminData) {
    try {
      const parsed = JSON.parse(adminData);
      token = parsed.access_token || parsed.token || '';
    } catch (e) {
      console.error('[API] Failed to parse admin token:', e);
    }
  }
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json'
  };
};
